import { type ReactNode } from 'react';
import { Card } from './Card';

interface PendingIntegrationCardProps {
  /** Nome da integração — aparece no título. Ex: "Tcal — Câmeras". */
  integration: string;
  /** Quem é responsável por destravar. Ex: "TI Galvani". */
  owner?: string;
  /** Previsão. Ex: "Pós-handover ago/2026". */
  eta?: string;
  /** Texto explicativo do que vai acontecer aqui quando estiver pronto. */
  description?: ReactNode;
  /** Link externo opcional pra abrir o sistema existente enquanto não há integração. */
  externalLink?: { label: string; href: string };
  className?: string;
}

/**
 * Cartão padrão pra módulos/seções que ainda dependem de integração externa.
 * Substitui telas "vazias" por um placeholder explícito e auditável.
 *
 * Use em M5 (Câmeras), M11 (IA), banner do M6 (Tetratech), etc.
 * Quando a integração for entregue, basta trocar este componente pelo conteúdo real.
 */
export function PendingIntegrationCard({
  integration,
  owner,
  eta,
  description,
  externalLink,
  className = '',
}: PendingIntegrationCardProps) {
  return (
    <Card padding="md" className={`flex flex-col gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        <span aria-hidden className="text-2xl">🔌</span>
        <div>
          <div className="text-xs uppercase tracking-wide text-jf-dim">Aguardando integração</div>
          <div className="text-base font-bold text-white">{integration}</div>
        </div>
      </div>

      {description && (
        <p className="text-sm text-jf-muted leading-relaxed">{description}</p>
      )}

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        {owner && (
          <div className="flex gap-1">
            <dt className="text-jf-dim">Responsável:</dt>
            <dd className="text-jf-muted font-semibold">{owner}</dd>
          </div>
        )}
        {eta && (
          <div className="flex gap-1">
            <dt className="text-jf-dim">Previsão:</dt>
            <dd className="text-jf-muted font-semibold">{eta}</dd>
          </div>
        )}
      </dl>

      {externalLink && (
        <a
          href={externalLink.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-jf-yellow text-xs font-bold hover:underline self-start"
        >
          {externalLink.label} ↗
        </a>
      )}
    </Card>
  );
}
